import { useParams, Link } from 'react-router-dom';
import { site } from '@/data/site';
import SEOHead from '@/components/base/SEOHead';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import FAQAccordion from '@/components/base/FAQAccordion';
import GameCard from '@/components/feature/GameCard';
import StickyDownloadBar from '@/components/feature/StickyDownloadBar';
import { getGameBySlug, getRelatedGames } from '@/data/games';
import NotFound from '@/pages/NotFound';

export default function GameDetail() {
  const { slug } = useParams<{ slug: string }>();
  const game = slug ? getGameBySlug(slug) : undefined;

  if (!game) {
    return <NotFound />;
  }

  const relatedGames = getRelatedGames(game.relatedGameSlugs).slice(0, 4);
  const downloadUrl = game.downloadUrl || site.affiliateUrl;
  const pageTitle = game.title;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={game.metaDescription}
        keywords={`${game.name}, ${game.name} Download, ${game.name} APK, ${game.category}, Yono Games, Android APK`}
      />

      <Header />

      <main className="pb-20">
        <div className="px-4 py-4">
          {/* Breadcrumb */}
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: 'Home', path: '/' },
                { label: 'Games', path: '/games' },
                { label: game.name },
              ]}
            />
          </div>

          {/* Game Summary */}
          <div className="flex items-start gap-3 mb-4">
            <img
              src={game.imageUrl}
              alt={game.name}
              width={60}
              height={60}
              className="w-[60px] h-[60px] rounded-2xl object-cover flex-shrink-0"
              onError={(e) => {
                const t = e.currentTarget;
                t.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.className = `w-[60px] h-[60px] rounded-2xl ${game.iconBg} flex items-center justify-center flex-shrink-0`;
                fallback.innerHTML = '<i class="ri-gamepad-line text-2xl text-white"></i>';
                t.parentNode?.insertBefore(fallback, t);
              }}
            />
            <div className="min-w-0 flex-1">
              <span className="inline-block px-2 py-0.5 bg-background-100 text-foreground-600 rounded-full text-[10px] font-medium mb-1">
                {game.category}
              </span>
              <h1 className="text-xl font-heading font-bold text-foreground-900 leading-tight mb-1">
                {game.name}
              </h1>
              <p className="text-foreground-600 text-[13px] leading-snug">
                {game.shortDescription}
              </p>
            </div>
          </div>

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { label: 'Welcome Bonus', value: game.welcomeBonus },
              { label: 'Min Deposit', value: game.minimumDeposit },
              { label: 'Min Withdrawal', value: game.minimumWithdrawal },
              { label: 'Platform', value: game.platform },
              { label: 'Requirement', value: game.androidRequirement },
              { label: 'Updated', value: game.updatedDate },
            ].map((info) => (
              <div key={info.label} className="bg-background-100 rounded-lg px-3 py-2">
                <span className="text-foreground-400 text-[10px]">{info.label}</span>
                <p className="text-foreground-800 text-[12px] font-medium">{info.value}</p>
              </div>
            ))}
          </div>

          {/* Bonus disclosure */}
          <p className="text-foreground-400 text-[10px] leading-relaxed mb-3">
            Bonus amounts, deposit requirements and withdrawal terms may change. Review the current offer terms before registering or depositing.
          </p>

          {/* Download Buttons */}
          <a
            href={downloadUrl}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            className="block w-full py-3 bg-primary-500 text-white rounded-xl text-sm font-semibold text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer mb-2"
          >
            <i className="ri-download-line mr-1.5"></i>
            Download {game.shortName} APK
          </a>
        </div>

        {/* Content Sections */}
        <div className="px-4 space-y-5 mt-6">
          {/* About */}
          <section>
            <h2 className="font-heading font-bold text-[16px] text-foreground-900 mb-2">
              About {game.name}
            </h2>
            <p className="text-foreground-600 text-[13px] leading-relaxed">{game.introduction}</p>
          </section>

          {/* Features */}
          <section>
            <h2 className="font-heading font-bold text-[16px] text-foreground-900 mb-2">
              {game.name} Features
            </h2>
            <div className="space-y-1.5">
              {game.features.map((feature, i) => (
                <div key={i} className="flex items-start gap-2">
                  <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-accent-600 text-[10px]"></i>
                  </div>
                  <span className="text-foreground-700 text-[13px]">{feature}</span>
                </div>
              ))}
            </div>
          </section>

          {/* How to Download */}
          <section>
            <h2 className="font-heading font-bold text-[16px] text-foreground-900 mb-2">
              How to Download {game.name}
            </h2>
            <p className="text-foreground-600 text-[13px] leading-relaxed">{game.downloadInstructions}</p>
          </section>

          {/* Registration and Bonus */}
          <section>
            <h2 className="font-heading font-bold text-[16px] text-foreground-900 mb-2">
              Registration and Bonus
            </h2>
            <p className="text-foreground-600 text-[13px] leading-relaxed">{game.registrationInstructions}</p>
            <p className="text-foreground-600 text-[13px] leading-relaxed mt-2">{game.bonusDescription}</p>
          </section>

          {/* Deposit and Withdrawal */}
          <section>
            <h2 className="font-heading font-bold text-[16px] text-foreground-900 mb-2">
              Deposit and Withdrawal
            </h2>
            <p className="text-foreground-600 text-[13px] leading-relaxed">{game.withdrawalDescription}</p>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="font-heading font-bold text-[16px] text-foreground-900 mb-3">
              Frequently Asked Questions
            </h2>
            <FAQAccordion items={game.faq} schema={true} />
          </section>

          {/* Related Games */}
          {relatedGames.length > 0 && (
            <section>
              <h2 className="font-heading font-bold text-[16px] text-foreground-900 mb-3">
                Related Yono Games
              </h2>
              <div className="grid grid-cols-2 gap-2.5">
                {relatedGames.map((g) => (
                  <GameCard key={g.id} game={g} />
                ))}
              </div>
            </section>
          )}

          {/* Bottom CTA */}
          <div className="bg-background-100 rounded-xl p-5 text-center">
            <p className="text-foreground-700 text-[13px] font-medium mb-3">
              Ready to play {game.name}?
            </p>
            <a
              href={downloadUrl}
              target="_blank"
              rel="sponsored nofollow noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-6 py-2.5 bg-primary-500 text-white rounded-full text-[13px] font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line"></i>
              Download {game.shortName} APK
            </a>
          </div>
        </div>
      </main>

      <StickyDownloadBar game={game} />
      <Footer />
    </>
  );
}